/* ==========================================================================
   FILE: modules/mind/components/AnalyticsPanel.jsx
   IAMONEAI - Analytics Panel Component
   Displays usage analytics and brain statistics
   ========================================================================== */

import React, { useMemo } from 'react';
import './AnalyticsPanel.css';

// ==========================================================================
// ANALYTICS PANEL COMPONENT
// ==========================================================================

/**
 * Displays user analytics including LLM usage, memory stats, and topics
 * 
 * @param {Object} props
 * @param {Object} analytics - Analytics data from API
 * @param {Object} brain - Brain data for additional stats
 * @param {string} period - Current period (e.g., "2025-12")
 * @param {Function} onPeriodChange - Called when period changes
 */
function AnalyticsPanel({
  analytics = {},
  brain = {},
  period,
  onPeriodChange,
  className = '',
}) {
  // ==========================================================================
  // COMPUTED VALUES
  // ==========================================================================

  const stats = useMemo(() => {
    return {
      totalCalls: analytics.total_llm_calls || 0,
      tokensIn: analytics.tokens_input || 0,
      tokensOut: analytics.tokens_output || 0,
      memoriesCreated: analytics.memories_created || 0,
      goalsCreated: analytics.goals_created || 0,
      
      // LLM breakdown
      gpt4o: analytics.llm_openai_gpt4o || 0,
      gpt4oMini: analytics.llm_openai_gpt4o_mini || 0,
      claude: analytics.llm_anthropic_claude || 0,
      byok: analytics.llm_user_byok || 0,
      
      // Purpose breakdown
      chatResponse: analytics.purpose_chat_response || 0,
      memoryExtract: analytics.purpose_memory_extract || 0,
      goalSteps: analytics.purpose_goal_steps || 0,
      
      // Epistemic breakdown
      facts: analytics.epistemic_fact || 0,
      opinions: analytics.epistemic_opinion || 0,
      beliefs: analytics.epistemic_belief || 0,
      
      // Topics
      topics: extractTopics(analytics),
    };
  }, [analytics]);

  const epistemicTotal = stats.facts + stats.opinions + stats.beliefs;
  const epistemicPercentages = {
    facts: epistemicTotal > 0 ? Math.round((stats.facts / epistemicTotal) * 100) : 0,
    opinions: epistemicTotal > 0 ? Math.round((stats.opinions / epistemicTotal) * 100) : 0,
    beliefs: epistemicTotal > 0 ? Math.round((stats.beliefs / epistemicTotal) * 100) : 0,
  };

  // ==========================================================================
  // RENDER
  // ==========================================================================

  const panelClasses = [
    'analytics-panel',
    className,
  ].filter(Boolean).join(' ');

  return (
    <div className={panelClasses}>
      {/* Header */}
      <div className="analytics-header">
        <h3 className="analytics-title">
          <ChartIcon />
          Brain Analytics
        </h3>
        <PeriodSelector 
          period={period} 
          onChange={onPeriodChange} 
        />
      </div>

      {/* Quick Stats */}
      <div className="analytics-quick-stats">
        <QuickStat
          icon="🧠"
          label="Memories"
          value={stats.memoriesCreated}
          subtext="this month"
        />
        <QuickStat
          icon="🎯"
          label="Goals"
          value={stats.goalsCreated}
          subtext="created"
        />
        <QuickStat
          icon="💬"
          label="AI Calls"
          value={stats.totalCalls}
          subtext="total"
        />
        <QuickStat
          icon="📊"
          label="Tokens"
          value={formatNumber(stats.tokensIn + stats.tokensOut)}
          subtext="used"
        />
      </div>

      {/* LLM Usage Section */}
      <div className="analytics-section">
        <h4 className="analytics-section-title">LLM Usage</h4>
        <div className="analytics-bar-chart">
          <BarChartItem
            label="GPT-4o (Chat)"
            value={stats.gpt4o}
            total={stats.totalCalls}
            color="#10B981"
          />
          <BarChartItem
            label="GPT-4o-mini (Memory)"
            value={stats.gpt4oMini}
            total={stats.totalCalls}
            color="#3B82F6"
          />
          {stats.claude > 0 && (
            <BarChartItem
              label="Claude"
              value={stats.claude}
              total={stats.totalCalls}
              color="#8B5CF6"
            />
          )}
          {stats.byok > 0 && (
            <BarChartItem
              label="Your API Key"
              value={stats.byok}
              total={stats.totalCalls}
              color="#F59E0B"
            />
          )}
        </div>
      </div>

      {/* Purpose Breakdown */}
      <div className="analytics-section">
        <h4 className="analytics-section-title">What AI Does</h4>
        <div className="analytics-purpose-grid">
          <PurposeCard
            icon="💬"
            label="Chat Responses"
            value={stats.chatResponse}
            description="Conversations"
          />
          <PurposeCard
            icon="💾"
            label="Memory Extraction"
            value={stats.memoryExtract}
            description="Facts learned"
          />
          <PurposeCard
            icon="📝"
            label="Goal Processing"
            value={stats.goalSteps}
            description="Goal analysis"
          />
        </div>
      </div>

      {/* Epistemic Breakdown */}
      <div className="analytics-section">
        <h4 className="analytics-section-title">How You Think</h4>
        <p className="analytics-section-desc">
          Breakdown of information types in your memories
        </p>
        
        <div className="epistemic-chart">
          <div className="epistemic-bar">
            <div 
              className="epistemic-segment epistemic-facts"
              style={{ width: `${epistemicPercentages.facts}%` }}
              title={`Facts: ${epistemicPercentages.facts}%`}
            />
            <div 
              className="epistemic-segment epistemic-opinions"
              style={{ width: `${epistemicPercentages.opinions}%` }}
              title={`Opinions: ${epistemicPercentages.opinions}%`}
            />
            <div 
              className="epistemic-segment epistemic-beliefs"
              style={{ width: `${epistemicPercentages.beliefs}%` }}
              title={`Beliefs: ${epistemicPercentages.beliefs}%`}
            />
          </div>
          
          <div className="epistemic-legend">
            <div className="epistemic-legend-item">
              <span className="epistemic-dot epistemic-facts" />
              <span>Facts ({epistemicPercentages.facts}%)</span>
            </div>
            <div className="epistemic-legend-item">
              <span className="epistemic-dot epistemic-opinions" />
              <span>Opinions ({epistemicPercentages.opinions}%)</span>
            </div>
            <div className="epistemic-legend-item">
              <span className="epistemic-dot epistemic-beliefs" />
              <span>Beliefs ({epistemicPercentages.beliefs}%)</span>
            </div>
          </div>
        </div>
      </div>

      {/* Topics */}
      {stats.topics.length > 0 && (
        <div className="analytics-section">
          <h4 className="analytics-section-title">Top Topics</h4>
          <div className="analytics-topics">
            {stats.topics.map((topic, index) => (
              <TopicTag
                key={topic.name}
                name={topic.name}
                count={topic.count}
                rank={index + 1}
              />
            ))}
          </div>
        </div>
      )}

      {/* Token Usage Details */}
      <div className="analytics-section analytics-section-muted">
        <h4 className="analytics-section-title">Token Usage</h4>
        <div className="token-stats">
          <div className="token-stat">
            <span className="token-stat-label">Input Tokens</span>
            <span className="token-stat-value">{formatNumber(stats.tokensIn)}</span>
          </div>
          <div className="token-stat">
            <span className="token-stat-label">Output Tokens</span>
            <span className="token-stat-value">{formatNumber(stats.tokensOut)}</span>
          </div>
          <div className="token-stat token-stat-total">
            <span className="token-stat-label">Total</span>
            <span className="token-stat-value">{formatNumber(stats.tokensIn + stats.tokensOut)}</span>
          </div>
        </div>
      </div>
    </div>
  );
}

// ==========================================================================
// SUB-COMPONENTS
// ==========================================================================

function PeriodSelector({ period, onChange }) {
  const currentMonth = new Date().toISOString().slice(0, 7);
  
  const periods = useMemo(() => {
    const result = [];
    const date = new Date();
    for (let i = 0; i < 6; i++) {
      const periodStr = date.toISOString().slice(0, 7);
      const label = date.toLocaleDateString('en-US', { month: 'short', year: 'numeric' });
      result.push({ value: periodStr, label });
      date.setMonth(date.getMonth() - 1);
    }
    return result;
  }, []);

  return (
    <select 
      className="period-selector"
      value={period || currentMonth}
      onChange={(e) => onChange?.(e.target.value)}
    >
      {periods.map(p => (
        <option key={p.value} value={p.value}>{p.label}</option>
      ))}
    </select>
  );
}

function QuickStat({ icon, label, value, subtext }) {
  return (
    <div className="quick-stat">
      <span className="quick-stat-icon">{icon}</span>
      <div className="quick-stat-content">
        <span className="quick-stat-value">{value}</span>
        <span className="quick-stat-label">{label}</span>
        {subtext && <span className="quick-stat-subtext">{subtext}</span>}
      </div>
    </div>
  );
}

function BarChartItem({ label, value, total, color }) {
  const percentage = total > 0 ? Math.round((value / total) * 100) : 0;
  
  return (
    <div className="bar-chart-item">
      <div className="bar-chart-header">
        <span className="bar-chart-label">{label}</span>
        <span className="bar-chart-value">{value} ({percentage}%)</span>
      </div>
      <div className="bar-chart-track">
        <div 
          className="bar-chart-fill"
          style={{ 
            width: `${percentage}%`,
            backgroundColor: color,
          }}
        />
      </div>
    </div>
  );
}

function PurposeCard({ icon, label, value, description }) {
  return (
    <div className="purpose-card">
      <span className="purpose-card-icon">{icon}</span>
      <span className="purpose-card-value">{value}</span>
      <span className="purpose-card-label">{label}</span>
      <span className="purpose-card-desc">{description}</span>
    </div>
  );
}

function TopicTag({ name, count, rank }) {
  const sizeClass = rank <= 2 ? 'topic-tag-lg' : rank <= 4 ? 'topic-tag-md' : 'topic-tag-sm';
  
  return (
    <span className={`topic-tag ${sizeClass}`}>
      {name}
      <span className="topic-tag-count">{count}</span>
    </span>
  );
}

// ==========================================================================
// HELPER FUNCTIONS
// ==========================================================================

function extractTopics(analytics) {
  const topics = [];
  const prefix = 'topic_';
  
  Object.entries(analytics).forEach(([key, value]) => {
    if (key.startsWith(prefix) && value > 0) {
      topics.push({
        name: key.replace(prefix, ''),
        count: value,
      });
    }
  });
  
  return topics.sort((a, b) => b.count - a.count).slice(0, 6);
}

function formatNumber(num) {
  if (num >= 1000000) {
    return (num / 1000000).toFixed(1) + 'M';
  }
  if (num >= 1000) {
    return (num / 1000).toFixed(1) + 'K';
  }
  return num.toString();
}

// ==========================================================================
// ICONS
// ==========================================================================

function ChartIcon() {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <line x1="18" y1="20" x2="18" y2="10" />
      <line x1="12" y1="20" x2="12" y2="4" />
      <line x1="6" y1="20" x2="6" y2="14" />
    </svg>
  );
}

// ==========================================================================
// EXPORTS
// ==========================================================================

export default AnalyticsPanel;